import { useEffect, useId, useState } from "react";
import {
  type RecurrenceFrequency,
  type RecurrenceRule,
} from "../../../../types/api";
import { Checkbox } from "../../../../components/Checkbox";
import { SelectPopover } from "../../../../components/SelectPopover";
import { TextInput } from "../../../../components/TextInput";
import { inclusiveDateToStartIso } from "../../../../utils/calendarTime";
import {
  FREQUENCY_OPTIONS,
  WEEKDAY_OPTIONS,
  describeRecurrence,
  describeRecurrenceHorizon,
  effectiveWeekdays,
  recurrenceEndDayKey,
  toggleWeekday,
  weekdayOfDayKey,
} from "./recurrenceRule";
import "./Itinerary.css";

interface RecurrenceFieldsProps {
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  value: RecurrenceRule;
  onChange: (rule: RecurrenceRule) => void;
  /**
   * The event's own first day (YYYY-MM-DD), used as the `min` on the end date. Repeating backwards
   * is not a thing, and the server would collapse it to a single occurrence without saying so.
   */
  firstDayKey?: string;
}

const FREQUENCY_VALUES = FREQUENCY_OPTIONS.map((o) => o.value);

/**
 * The "repeat this event" control: how often, every how many periods, and until when.
 *
 * <b>The series ends on a date, not after a number of repeats.</b> A coach knows "every Tuesday
 * until the end of October"; they do not know that is nine times, and asking them to count it is
 * asking them to do arithmetic the server is about to do anyway. The end date is optional, because
 * often the answer really is "for now" — the server then materializes a fixed horizon of
 * occurrences, and the hint below says so rather than implying the series is endless.
 *
 * Built from the project's control primitives (`Checkbox`, `SelectPopover`, `TextInput`) rather than
 * native elements, so the spacing, borders, focus rings and popover idiom match the rest of the app
 * — see "Design system" in web/CLAUDE.md.
 *
 * The group has an `aria-label` rather than a visible heading. The checkbox immediately above
 * already says "Repeat this event", so a "Repeat" caption between the two was a second, redundant
 * name for the same thing — and being an inline `<span>` in a block field, it rendered jammed
 * against the checkbox's own label.
 */
export const RecurrenceFields = ({
  enabled,
  onEnabledChange,
  value,
  onChange,
  firstDayKey,
}: RecurrenceFieldsProps) => {
  const fieldId = useId();

  // The interval is held as RAW TEXT while the coach is typing. `Number("")` is 0, so writing the
  // parsed value straight back would replace a cleared field with a "0" under the cursor and make
  // the summary read "Every 0 weeks". The parent keeps the last parseable value, which is what the
  // summary and the save both read.
  const [intervalText, setIntervalText] = useState(String(value.interval));

  // Re-sync when the parent resets the rule (a new event, or the toggle going off and on).
  useEffect(() => {
    setIntervalText(String(value.interval));
  }, [value.interval]);

  const commit = (patch: Partial<RecurrenceRule>) => onChange({ ...value, ...patch });

  const endDayKey = recurrenceEndDayKey(value);
  const horizon = describeRecurrenceHorizon(value);

  // The start day is the first occurrence, so it is always on and cannot be switched off. It is
  // derived from the event's date on every render rather than stored, so moving the start date
  // moves the locked day with it.
  const startDay = weekdayOfDayKey(firstDayKey);
  const selectedDays = effectiveWeekdays(value.daysOfWeek, startDay);
  const startDayLabel = startDay === null ? "" : WEEKDAY_OPTIONS[startDay].label;

  return (
    <div className="event-editor__field">
      <Checkbox
        label="Repeat this event"
        checked={enabled}
        onChange={(e) => onEnabledChange(e.target.checked)}
        data-testid="recurrence-toggle"
      />

      {enabled && (
        <>
          <div
            className="recurrence-fields"
            role="group"
            aria-label="Repeat settings"
            data-testid="recurrence-fields"
          >
            <div className="recurrence-fields__field">
              {/*
                `.text-input__label` deliberately, not a class of our own: the two fields beside
                this one are `TextInput`s, which render their labels with exactly that class, and
                `.event-editor__field label:not(.text-input__label)` in ScheduleSection.css
                restyles anything else to 12px/700 grey. A hand-written label without it came out
                bolder and shorter than its neighbours, which pushed this column's control out of
                line with theirs.
              */}
              <label className="text-input__label" htmlFor={`${fieldId}-frequency`}>
                Repeats
              </label>
              <SelectPopover
                id={`${fieldId}-frequency`}
                options={FREQUENCY_VALUES}
                value={value.frequency}
                onChange={(frequency) =>
                  commit({ frequency: frequency as RecurrenceFrequency })
                }
                placeholder="Weekly"
                // A required choice: the placeholder is not a value the rule can hold.
                clearable={false}
                testId="recurrence-frequency"
                fullWidth
              />
            </div>

            <TextInput
              className="recurrence-fields__field"
              label="Every"
              id={`${fieldId}-interval`}
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={intervalText}
              onChange={(e) => {
                setIntervalText(e.target.value);
                const parsed = Number(e.target.value);
                // >= 1, not merely an integer: `min={1}` is not enforced while typing, so a bare
                // integer check let 0 and negatives through and the summary read "Every 0 weeks".
                if (Number.isInteger(parsed) && parsed >= 1) {
                  commit({ interval: parsed });
                }
              }}
              onBlur={() => setIntervalText(String(value.interval))}
              data-testid="recurrence-interval"
            />
          </div>

          {/*
            Weekly only: daily already covers every day, and the server refuses weekdays on a daily
            or monthly repeat. The editor renders this whole control on create only, so an existing
            series never shows toggles that could not change it.
          */}
          {value.frequency === "Weekly" && (
            <div
              className="recurrence-days"
              role="group"
              aria-labelledby={`${fieldId}-days-label`}
              data-testid="recurrence-days"
            >
              <span id={`${fieldId}-days-label`} className="text-input__label">
                Repeat on
              </span>
              <div className="recurrence-days__buttons">
                {WEEKDAY_OPTIONS.map((day) => {
                  const locked = day.value === startDay;
                  return (
                    <button
                      key={day.value}
                      type="button"
                      className="recurrence-days__day"
                      aria-label={day.label}
                      aria-pressed={selectedDays.includes(day.value)}
                      aria-disabled={locked || undefined}
                      aria-describedby={locked ? `${fieldId}-days-hint` : undefined}
                      onClick={() => {
                        if (locked) return;
                        commit({ daysOfWeek: toggleWeekday(value.daysOfWeek, day.value, startDay) });
                      }}
                      data-testid={`recurrence-day-${day.value}`}
                    >
                      {day.short}
                    </button>
                  );
                })}
              </div>
              {startDayLabel && (
                <div id={`${fieldId}-days-hint`} className="event-editor__hint">
                  {startDayLabel} is the day this event starts, so it is always included.
                </div>
              )}
            </div>
          )}

          <TextInput
            className="recurrence-fields__end"
            label={
              <>
                Until <span className="itin-field__optional">(optional)</span>
              </>
            }
            id={`${fieldId}-until`}
            type="date"
            min={firstDayKey || undefined}
            value={endDayKey}
            onChange={(e) => {
              // Day-grained UTC midnight, and the day itself is INCLUSIVE — the last day the
              // series runs, not the day after it stops. `|| null` because
              // `inclusiveDateToStartIso` yields "" for anything it cannot parse, and "" would
              // read as falsy to the field (so it would look cleared) while still travelling in
              // the payload — where the server rejects it as a bad date.
              commit({ untilDateUtc: inclusiveDateToStartIso(e.target.value) || null });
            }}
            data-testid="recurrence-until"
          />

          <div className="event-editor__hint" data-testid="recurrence-summary">
            {describeRecurrence(value, firstDayKey)}
            {horizon && ` ${horizon}`}
          </div>
        </>
      )}
    </div>
  );
};
